import { useCallback, useEffect, useRef } from "react";
import { FullCommunityContent } from "./OnboardingAPI";

// Bump when the persisted shape changes in a way old code can't read.
// Loads with a different version are discarded instead of crashing.
const DRAFT_VERSION = 1;
const SAVE_DEBOUNCE_MS = 600;

export type OnboardingDraftSnapshot = {
  step: number;
  sourceTab: "link" | "paste" | "upload";
  websiteUrl: string;
  pastedContent: string;
  fullContent: FullCommunityContent | null;
  enabledHabits: boolean[];
  enabledPlans: boolean[];
  enabledQuotes: boolean[];
  selectedDefaultVoiceId: string;
  draftTitle: string;
  draftDescription: string;
  draftShortDescription: string;
  draftCategoryName: string;
  draftTags: string;
  draftWritingStyle: string;
  draftVisualStyle: string;
  draftAuthorDescription: string;
  draftAiGuidelines: string;
  draftPrimaryColor: string;
  draftSecondaryColor: string;
};

type StoredDraft = OnboardingDraftSnapshot & {
  version: number;
  savedAt: number;
};

const storageKey = (userId: string) =>
  `noble.onboarding-draft.${userId || "anon"}`;

// A snapshot is only worth persisting (or offering to resume) when the coach
// has actually typed or generated something. File uploads are intentionally
// not persisted (File objects don't survive a refresh).
export function snapshotHasContent(snap: OnboardingDraftSnapshot): boolean {
  return (
    !!snap.websiteUrl.trim() ||
    !!snap.pastedContent.trim() ||
    !!snap.fullContent
  );
}

export function useOnboardingDraft(userId: string) {
  const key = storageKey(userId);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, []);

  const load = useCallback((): OnboardingDraftSnapshot | null => {
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as StoredDraft;
      if (!parsed || parsed.version !== DRAFT_VERSION) return null;
      return parsed;
    } catch {
      return null;
    }
  }, [key]);

  const save = useCallback(
    (snap: OnboardingDraftSnapshot) => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => {
        try {
          const stored: StoredDraft = {
            ...snap,
            version: DRAFT_VERSION,
            savedAt: Date.now(),
          };
          window.localStorage.setItem(key, JSON.stringify(stored));
        } catch {
          // Storage full or unavailable — autosave is best-effort only.
        }
      }, SAVE_DEBOUNCE_MS);
    },
    [key]
  );

  const clear = useCallback(() => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    try {
      window.localStorage.removeItem(key);
    } catch {
      // ignore
    }
  }, [key]);

  return { load, save, clear };
}
